import { Link } from "react-router-dom";
import { ArrowUpRight, Lock } from "lucide-react";
import PageIntro from "@/components/PageIntro";
import Reveal, { RevealGroup, RevealItem } from "@/components/Reveal";

const SECTIONS = [
  {
    n: "01",
    t: "Engineering architecture",
    b: "Institutional Role Nodes, a Role-to-Role lifecycle protocol, the Control Decision Envelope, a shared proof & receipt layer, and execution / interoperability adapters. KATLAS records the governed action — it is not a central store of raw private evidence.",
  },
  {
    n: "02",
    t: "Control Decision Envelope",
    b: "A standardisable object consumed before a consequential action executes, expressing authority, policy, evidence references, decision and privacy boundary. Outcomes: allowed, blocked, review required, released, refused.",
  },
  {
    n: "03",
    t: "Data custody and trust boundaries",
    b: "Evidence remains with its custodian. Only metadata, roles, permissions and evidence references cross the boundary. Raw private data and withheld assets are never sent to the node.",
  },
  {
    n: "04",
    t: "Authority and policy model",
    b: "Stakeholder-defined roles, mandates, scopes and purposes. Authority gates are explicit and visible. Policy and current-state conditions are evaluated at the point of action. Institutional policy and authorised roles decide; KATLAS makes that boundary executable.",
  },
  {
    n: "05",
    t: "Receipt model",
    b: "A public-safe receipt records who acted, when, under what authority, which evidence references were used and what was withheld — with a verification status. Full signed envelopes are not exposed publicly.",
  },
  {
    n: "06",
    t: "Integration patterns",
    b: "Conceptual patterns only — REST APIs, backend-only authentication, execution adapters, and event / webhook integration where actually supported. Standards examples where relevant: ISO 20022 (value movement) and FHIR (health records). No specific production APIs or connectors are claimed here.",
  },
  {
    n: "07",
    t: "Authentication and secrets",
    b: "Any node credential is backend-only (server-side). The public website never holds or requests it, never calls the signing node, and never generates signed receipts. Legacy credential aliases are prohibited.",
  },
  {
    n: "08",
    t: "Deployment modes",
    b: "Showcase, governed sandbox and pilot. Simulated operation demonstrates governed journeys with synthetic data; live-node operation is provisioned separately and never runs on the marketing site.",
  },
  {
    n: "09",
    t: "Sandbox model",
    b: "A synthetic but realistic governed journey with separate role identities and devices. Access is provisioned on request via a governed process — not self-serve — until automated, governed provisioning exists.",
  },
  {
    n: "10",
    t: "Pilot acceptance framework",
    b: "One bounded problem and one consequential handoff, with agreed acceptance criteria and the measurement framework below. Outputs: role-network blueprint, receipt model and operational acceptance evidence.",
  },
];

const MEASURES = [
  "Time from exception to accountable owner",
  "First-time condition pass rate",
  "Manual reconciliation steps",
  "Time to prove readiness",
  "Unresolved authority exceptions",
  "Evidence shared versus withheld",
  "Receipt verification rate",
  "Time from proposed change to safe release",
];

const CONTROLLED_DOCS = [
  "Sample statement of work (SOW)",
  "Data processing agreement (DPA) template",
  "Security questionnaire pack",
  "Pricing options",
  "Detailed pilot acceptance criteria",
];

export default function TechnicalAppendix() {
  return (
    <div data-testid="technical-appendix-page">
      <PageIntro
        index="/technical-appendix"
        eyebrow="Technical appendix"
        titleLines={["The architecture,", "boundaries and", "integration model."]}
        lead="A public, ungated reference for architecture, security and assurance reviewers. It describes conceptual patterns and boundaries. It does not claim specific production deployments, connectors or certifications."
        meta={["Public & ungated", "Architecture", "Integration", "Assurance"]}
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="divide-y divide-ink/12 border-y border-ink/12">
            {SECTIONS.map((s) => (
              <Reveal key={s.n} className="grid grid-cols-1 gap-6 py-10 md:grid-cols-12 md:gap-10">
                <div className="md:col-span-4">
                  <span className="font-mono text-xs text-teal">{s.n}</span>
                  <h2 className="mt-3 font-serif text-2xl leading-snug tracking-tight text-ink md:text-3xl">
                    {s.t}
                  </h2>
                </div>
                <p className="font-sans text-base leading-relaxed text-ink/75 md:col-span-8 md:pt-2">
                  {s.b}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Measurement framework */}
      <section className="border-t border-ink/12 bg-stone py-20 md:py-28" data-testid="measurement-framework">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-10 flex items-center gap-4">
            <span className="font-mono text-xs tracking-[0.24em] text-teal">§ measurement</span>
            <span className="h-px w-10 bg-ink/25" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink/45">
              Pilot measurement framework
            </span>
          </div>
          <p className="max-w-2xl font-sans text-base leading-relaxed text-ink/70 md:text-lg">
            Pilots are measured against agreed categories rather than pre-stated ROI figures.
            KATLAS does not claim to have achieved these outcomes unless evidence is supplied.
          </p>
          <RevealGroup className="mt-10 grid grid-cols-1 gap-px border border-ink/15 bg-ink/15 sm:grid-cols-2 lg:grid-cols-4">
            {MEASURES.map((m) => (
              <RevealItem key={m} className="bg-stone p-6">
                <p className="font-sans text-sm leading-relaxed text-ink/80">{m}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* Controlled buyer documents */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-10 flex items-center gap-4">
            <Lock className="h-4 w-4 text-ink/50" strokeWidth={1.75} />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink/45">
              Controlled documents · available on request
            </span>
          </div>
          <RevealGroup className="grid grid-cols-1 gap-px border border-ink/15 bg-ink/15 sm:grid-cols-2 lg:grid-cols-3">
            {CONTROLLED_DOCS.map((d) => (
              <RevealItem key={d} className="bg-stone p-7">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/45">
                  Placeholder
                </p>
                <h3 className="mt-3 font-serif text-lg text-ink">{d}</h3>
                <p className="mt-2 font-sans text-sm text-ink/60">
                  Provided under NDA during a pilot engagement — not published here.
                </p>
              </RevealItem>
            ))}
          </RevealGroup>

          <Reveal delay={0.2} className="mt-12">
            <Link
              to="/company#contact"
              data-testid="appendix-cta"
              className="group inline-flex items-center gap-2 bg-teal px-6 py-3.5 font-sans text-sm font-medium text-stone transition-colors duration-300 hover:bg-ink"
            >
              Request controlled documents
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
